import type { CSSProperties, HTMLAttributes } from 'react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { Button } from '../core/Button';
import { SectionLabel } from '../core/SectionLabel';
import { MissionRow, type MissionRowProps, type MissionRowVariant } from './MissionRow';
import { SortableMissionRow } from './SortableMissionRow';

export interface MissionListItem extends Omit<MissionRowProps, 'variant' | 'reordering'> {
  readonly id: string;
}

export interface MissionListProps extends Omit<HTMLAttributes<HTMLElement>, 'style'> {
  readonly label?: string;
  readonly actionLabel?: string;
  readonly missions?: readonly MissionListItem[];
  readonly variant?: MissionRowVariant;
  readonly onAction?: (() => void) | undefined;
  readonly onMission?: (mission: MissionListItem, index: number) => void;
  readonly showTimes?: boolean;
  readonly dimPriorities?: boolean;
  readonly dimDurations?: boolean;
  readonly style?: CSSProperties;
  /**
   * Mode réorganisation : les lignes deviennent glissables (`@dnd-kit`), le `DndContext` et ses
   * capteurs restent la responsabilité de l'appelant (`TodayScreen`) — `MissionList` ne fournit
   * que le `SortableContext` interne, cohérent avec la liste d'identifiants affichée.
   */
  readonly reordering?: boolean;
  readonly onMoveMission?: (id: string, direction: -1 | 1) => void;
}

/** « MISSIONS DU JOUR » : en-tête + action RÉORGANISER + lignes de mission. */
export function MissionList({
  label = 'Missions du jour',
  actionLabel = 'Réorganiser',
  missions = [],
  variant = 'desktop',
  onAction,
  onMission,
  showTimes = true,
  dimPriorities = false,
  dimDurations = false,
  style,
  reordering = false,
  onMoveMission,
  ...rest
}: MissionListProps) {
  const rows = missions.map((mission, index) =>
    reordering ? (
      <SortableMissionRow
        key={mission.id}
        variant={variant}
        {...mission}
        reordering
        position={index + 1}
        total={missions.length}
        onMoveUp={() => onMoveMission?.(mission.id, -1)}
        onMoveDown={() => onMoveMission?.(mission.id, 1)}
        style={index === missions.length - 1 ? { borderBottom: 0 } : undefined}
      />
    ) : (
      <MissionRow
        key={mission.id}
        variant={variant}
        {...mission}
        showTimes={showTimes}
        dimPriorities={dimPriorities}
        dimDurations={dimDurations}
        onAction={mission.onAction ?? (() => onMission?.(mission, index))}
        style={index === missions.length - 1 ? { borderBottom: 0 } : undefined}
      />
    ),
  );

  return (
    <section
      {...rest}
      style={{
        background: 'var(--surface-panel)',
        border: '1px solid rgba(255,255,255,.07)',
        borderRadius: 'var(--radius-panel)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-panel)',
        ...style,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          padding: '16px var(--pad-row-x) 14px',
        }}
      >
        <SectionLabel size={11}>{label}</SectionLabel>
        <Button size="md" variant="solid" onClick={onAction}>
          {actionLabel}
        </Button>
      </header>
      {reordering ? (
        <SortableContext items={missions.map((mission) => mission.id)} strategy={verticalListSortingStrategy}>
          <div>{rows}</div>
        </SortableContext>
      ) : (
        <div>{rows}</div>
      )}
    </section>
  );
}
